/**
 * Ultrasonic session join -- one of two equal doors.
 *
 * Differential 8-FSK, 200 Hz tone spacing, matching
 * `tools/ultrasonic-bench.html`: sync(3) + 5 characters x 2 symbols + checksum.
 * The differential step is what lets a change-detector segment symbols without
 * clock recovery -- see the caveat on `encode`, which is load-bearing.
 *
 * !! This header used to claim the `+1` step "guarantees consecutive tones always
 * differ". It does not, and that false invariant is exactly why the bug below
 * survived: the property the whole receiver depends on was asserted in prose and
 * never tested. [M] 55.12% of the code space was undecodable as a result. Codes
 * are now filtered at generation (`meetfolks/joincode.is_chirpable`) so this path
 * only ever sees codes it can carry; the modulation itself is still wrong.
 *
 * Read §9a before treating a decode failure as a bug. [F] Reception worked on ~90%
 * of phones tested -- a good per-device number, and the wrong number to design
 * against, because a session needs *every* participant to join. [D] At a 5-person
 * table roughly **4 sessions in 10** have someone who cannot join by sound. So the
 * typed code is not a fallback for when this breaks; it is the other half of a
 * normal flow, and the UI shows both together with no error styling.
 *
 * [T] Two things about that 90% are unvalidated: whether failures cluster on
 * particular device models (if so a blocklist beats a UX path), and whether it
 * holds at table geometry -- 18-19 kHz is directional and easily absorbed, so
 * phones lying flat with a hand or laptop in the path is the condition that
 * matters, not two phones facing each other.
 */

import { JOIN_CODE_ALPHABET, JOIN_CODE_LENGTH } from "./const.js";

export const N_TONES = 8;
export const TONE_SPACING_HZ = 200;
export const DEFAULT_BASE_HZ = 18000;
export const SYMBOL_SEC = 0.06;
const SYNC_SYMBOLS = 3;

/** Code -> differential tone indices, including sync and checksum. */
export function encode(code, alphabet = JOIN_CODE_ALPHABET) {
  const normalised = code.trim().toUpperCase();
  const symbols = [];
  for (const ch of normalised) {
    const index = alphabet.indexOf(ch);
    if (index < 0) throw new Error(`character ${ch} is not in the join alphabet`);
    // 32 symbols = two octal digits, so every payload symbol fits one of 8 tones.
    symbols.push(index >> 3, index & 7);
  }
  const checksum = symbols.reduce((a, b) => a + b, 0) % N_TONES;
  const payload = [...Array(SYNC_SYMBOLS).fill(0), ...symbols, checksum];

  // Differential: each tone is the previous one stepped by 1 + symbol, so a change
  // detector can find symbol boundaries without recovering a clock.
  //
  // BUG, mitigated elsewhere rather than fixed here: for `s === N_TONES - 1` the
  // step is `N_TONES`, which is 0 mod N_TONES, so the tone *repeats*. `listen`
  // records a tone only when it changes (see `candidate !== lastTone`), so it
  // cannot see the second of a pair: the frame arrives a symbol short, the
  // differential chain shifts, and sync and checksum reject it -- identically on
  // every repeat, so waiting never helps.
  //
  // `meetfolks/joincode.is_chirpable` keeps such codes from ever being issued, and
  // `tests/web_chirp_probe.mjs` pins that. The real repair is a 9-tone carrier
  // whose step is never 0, which changes the wire and so waits for a deliberate
  // protocol revision. Do not "simplify" the generator filter away before then.
  const tones = [];
  let previous = 0;
  for (const s of payload) {
    previous = (previous + 1 + s) % N_TONES;
    tones.push(previous);
  }
  return tones;
}

export function decode(tones, alphabet = JOIN_CODE_ALPHABET) {
  const expected = SYNC_SYMBOLS + JOIN_CODE_LENGTH * 2 + 1;
  if (tones.length < expected) return null;
  const window = tones.slice(-expected);

  const symbols = [];
  let previous = 0;
  for (const tone of window) {
    symbols.push((tone - previous - 1 + N_TONES * 2) % N_TONES);
    previous = tone;
  }
  const sync = symbols.slice(0, SYNC_SYMBOLS);
  if (sync.some((s) => s !== 0)) return null;

  const payload = symbols.slice(SYNC_SYMBOLS, SYNC_SYMBOLS + JOIN_CODE_LENGTH * 2);
  const checksum = symbols[symbols.length - 1];
  if (payload.reduce((a, b) => a + b, 0) % N_TONES !== checksum) return null;

  let code = "";
  for (let i = 0; i < payload.length; i += 2) {
    const index = (payload[i] << 3) | payload[i + 1];
    if (index >= alphabet.length) return null;
    code += alphabet[index];
  }
  return code;
}

export const toneFrequency = (index, baseHz = DEFAULT_BASE_HZ) => baseHz + index * TONE_SPACING_HZ;

/** Transmit a code. Resolves when the last tone has finished. */
export async function transmit(
  code,
  { baseHz = DEFAULT_BASE_HZ, symbolSec = SYMBOL_SEC, gain = 0.2, ctx: shared = null } = {}
) {
  const tones = encode(code);
  // A caller repeating this accepts a context and keeps it: on iOS every fresh
  // playback context can renegotiate the audio session, and doing that every few
  // seconds while the microphone is live is asking for the route to change
  // underneath a recording.
  const ctx = shared ?? new AudioContext();
  await ctx.resume();

  const start = ctx.currentTime + 0.1;
  const master = ctx.createGain();
  master.gain.value = gain;
  master.connect(ctx.destination);

  tones.forEach((tone, i) => {
    const osc = ctx.createOscillator();
    osc.frequency.value = toneFrequency(tone, baseHz);
    const env = ctx.createGain();
    // Short ramps: a hard start on an 18 kHz tone splatters energy across the
    // band and smears the neighbouring symbol.
    const t0 = start + i * symbolSec;
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(1, t0 + 0.005);
    env.gain.setValueAtTime(1, t0 + symbolSec - 0.005);
    env.gain.linearRampToValueAtTime(0, t0 + symbolSec);
    osc.connect(env).connect(master);
    osc.start(t0);
    osc.stop(t0 + symbolSec);
  });

  const total = 0.15 + tones.length * symbolSec;
  await new Promise((r) => setTimeout(r, total * 1000));
  if (!shared) await ctx.close();
  return { tones, durationSec: total };
}

/**
 * Listen for a chirped code.
 *
 * Returns `{code, stop}`; `code` is a promise resolving to the decoded string, or
 * null on timeout. The listener tracks the strongest bin in the band and records a
 * tone only when it *changes*, which is what the differential encoding buys.
 */
export function listen({
  baseHz = DEFAULT_BASE_HZ,
  timeoutSec = 25,
  minSnrDb = 6,
  onTone = null,
} = {}) {
  let stopped = false;
  let stopFn = () => {
    stopped = true;
  };

  const code = (async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        // Every one of these mangles an 18 kHz carrier if the browser applies it.
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;
    source.connect(analyser);

    const bins = new Float32Array(analyser.frequencyBinCount);
    const binHz = ctx.sampleRate / analyser.fftSize;
    const toneBins = Array.from({ length: N_TONES }, (_, i) =>
      Math.round(toneFrequency(i, baseHz) / binHz)
    );

    const tones = [];
    let lastTone = -1;
    let stableCount = 0;
    let candidate = -1;
    const deadline = performance.now() + timeoutSec * 1000;

    stopFn = () => {
      stopped = true;
    };

    try {
      while (!stopped && performance.now() < deadline) {
        analyser.getFloatFrequencyData(bins);

        let best = -1;
        let bestDb = -Infinity;
        let secondDb = -Infinity;
        toneBins.forEach((bin, i) => {
          const db = Math.max(bins[bin] ?? -Infinity, bins[bin - 1] ?? -Infinity, bins[bin + 1] ?? -Infinity);
          if (db > bestDb) {
            secondDb = bestDb;
            bestDb = db;
            best = i;
          } else if (db > secondDb) {
            secondDb = db;
          }
        });

        // Require the winner to stand clear of the runner-up, or room noise wins.
        const clear = Number.isFinite(secondDb) ? bestDb - secondDb >= minSnrDb : true;
        if (clear && best >= 0) {
          if (best === candidate) {
            stableCount++;
          } else {
            candidate = best;
            stableCount = 1;
          }
          // Two consecutive agreeing frames is enough at ~5 ms per frame against a
          // 60 ms symbol, and rejects single-frame noise spikes.
          if (stableCount === 2 && candidate !== lastTone) {
            lastTone = candidate;
            tones.push(candidate);
            onTone?.(candidate, tones.length);
            const decoded = decode(tones);
            if (decoded) return decoded;
          }
        }
        await new Promise((r) => setTimeout(r, 5));
      }
      return null;
    } finally {
      stream.getTracks().forEach((t) => t.stop());
      await ctx.close();
    }
  })();

  return { code, stop: () => stopFn() };
}

/**
 * Highest frequency this device's speaker-to-mic round trip actually passes.
 *
 * The failure this catches is a 16 kHz-capped input: ultrasonic is then impossible
 * and completely undetectable without probing, so such a device should be routed
 * to the typed code *before* it tries and fails. The product doc flags exactly
 * this as a known risk.
 *
 * Measurement discipline, learned from the bench's own bug (`tools/README.md`):
 * the response is sampled **only while a probe tone is playing**. The bench kept
 * absorbing audio after its sweep ended, so speech and room noise -- far louder at
 * low frequencies than an ultrasonic response -- inflated its max-hold and made
 * the reported ceiling *drift downward* the longer the mic stayed open, enough to
 * flip a healthy device to "ultrasonic impossible".
 */
export async function probeMicCeiling({
  fromHz = 15000,
  toHz = 21000,
  stepHz = 250,
  toneMs = 70,
  minSnrDb = 8,
  gain = 0.25,
  onStep = null,
} = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  });
  const ctx = new AudioContext();
  try {
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 4096;
    analyser.smoothingTimeConstant = 0;
    source.connect(analyser);

    const bins = new Float32Array(analyser.frequencyBinCount);
    const binHz = ctx.sampleRate / analyser.fftSize;
    const nyquist = ctx.sampleRate / 2;

    const readBin = (hz) => {
      analyser.getFloatFrequencyData(bins);
      const bin = Math.round(hz / binHz);
      return Math.max(bins[bin] ?? -Infinity, bins[bin - 1] ?? -Infinity, bins[bin + 1] ?? -Infinity);
    };

    const responses = [];
    let ceiling = 0;

    for (let hz = fromHz; hz <= Math.min(toHz, nyquist - binHz * 2); hz += stepHz) {
      // Silence first: the noise floor at *this* frequency, not a global one.
      const floor = readBin(hz);

      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.frequency.value = hz;
      env.gain.value = gain;
      osc.connect(env).connect(ctx.destination);
      osc.start();
      await new Promise((r) => setTimeout(r, toneMs));
      const level = readBin(hz);
      osc.stop();
      osc.disconnect();
      env.disconnect();
      // Settle so this tone's ring-out is not measured as the next tone's floor.
      await new Promise((r) => setTimeout(r, 25));

      const snr = level - floor;
      responses.push({ hz, level, floor, snr });
      onStep?.({ hz, snr });
      if (snr >= minSnrDb) ceiling = hz;
    }

    return {
      ceilingHz: ceiling,
      sampleRate: ctx.sampleRate,
      nyquistHz: nyquist,
      responses,
      // The carrier the transmitter should use: a little below the ceiling, with
      // room for all 8 tones.
      suggestedBaseHz: ceiling ? Math.max(fromHz, ceiling - N_TONES * TONE_SPACING_HZ) : 0,
      ultrasonicUsable: ceiling >= DEFAULT_BASE_HZ + N_TONES * TONE_SPACING_HZ,
    };
  } finally {
    stream.getTracks().forEach((t) => t.stop());
    await ctx.close();
  }
}
